/* ============================================================
   storage — the folder tree.

   Both drives are roots. Children load when a node is opened
   and are cached after that, so walking back up a tree is
   instant and a drive nobody opened costs nothing.

   A secure folder someone else made still appears — that is the
   rule: you can see it exists — but it cannot be expanded, and
   says why.
   ============================================================ */

import React, { useCallback, useEffect, useState } from 'react';
import { Icon, fmtBytes } from './lib';
import { api } from './api';

function Node({ token, drive, folder, depth, path, onNavigate, cache, setCache, dropTarget, onDropFiles }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const key = `${drive}${folder.path}`;
  const children = cache[key];
  const active = path === folder.path;
  const onTrail = path.startsWith(folder.path + '/');

  useEffect(() => { if (onTrail) setOpen(true); }, [onTrail]);

  const load = useCallback(async () => {
    if (children || folder.locked) return;
    setLoading(true);
    try {
      const rows = await api.folders(token, drive, folder.path);
      setCache((c) => ({ ...c, [key]: rows }));
    } catch { setCache((c) => ({ ...c, [key]: [] })); }
    finally { setLoading(false); }
  }, [children, folder.locked, folder.path, token, drive, key, setCache]);

  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <li className="st-tree-node">
      <div
        className={`st-tree-row ${active ? 'is-active' : ''} ${dropTarget === folder.path ? 'is-drop' : ''}`}
        style={{ paddingLeft: `${8 + depth * 14}px` }}
        onDragOver={(e) => { if (!folder.locked) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } }}
        onDrop={(e) => { if (!folder.locked) onDropFiles(e, folder.path); }}
      >
        <button
          type="button"
          className={`st-tree-twist ${open ? 'is-open' : ''}`}
          aria-label={open ? 'Collapse' : 'Expand'}
          aria-expanded={open}
          disabled={folder.locked}
          onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        >
          <Icon name="chevron" size={12}/>
        </button>
        <button type="button" className="st-tree-label" onClick={() => onNavigate(drive, folder.path)}
                title={folder.locked ? 'Secure — only its owner and admins can open it' : folder.path}>
          <Icon name={folder.secure ? 'folderLock' : 'folder'} size={14}/>
          <span className="st-tree-name">{folder.name}</span>
          {folder.locked && <Icon name="lock" size={11} className="st-tree-lock"/>}
        </button>
      </div>
      {open && (
        <ul className="st-tree-children">
          {loading && [0, 1].map((i) => (
            <li key={i} className="st-tree-skel" aria-hidden="true" style={{ paddingLeft: 14 + depth * 12 }}>
              <span className="skeleton st-sk st-sk--line"/>
            </li>
          ))}
          {!loading && children?.length === 0 && <li className="st-tree-empty meta">empty</li>}
          {!loading && children?.map((c) => (
            <Node key={c.id} token={token} drive={drive} folder={c} depth={depth + 1} path={path}
                  onNavigate={onNavigate} cache={cache} setCache={setCache}
                  dropTarget={dropTarget} onDropFiles={onDropFiles}/>
          ))}
        </ul>
      )}
    </li>
  );
}

export default function Tree({
  token, drives, drivesLoading, drive, path, onNavigate, refreshKey, dropTarget, onDropFiles, onOpenDrives,
  trash, trashActive, onOpenTrash, onDropTrash,
}) {
  const [trashOver, setTrashOver] = useState(false);
  const [cache, setCache] = useState({});
  const [roots, setRoots] = useState({});

  useEffect(() => { setCache({}); }, [refreshKey]);

  useEffect(() => {
    let live = true;
    drives.filter((d) => d.online).forEach((d) => {
      api.folders(token, d.id, '/')
        .then((rows) => { if (live) setRoots((r) => ({ ...r, [d.id]: rows })); })
        .catch(() => { if (live) setRoots((r) => ({ ...r, [d.id]: [] })); });
    });
    return () => { live = false; };
  }, [token, drives, refreshKey]);

  return (
    <nav className="st-tree" aria-label="Folders" aria-busy={!!drivesLoading}>
      {drivesLoading && [0, 1, 2].map((i) => (
        <div key={i} className="st-tree-drive" aria-hidden="true">
          <div className="st-drivehead st-drivehead--skel">
            <span className="skeleton st-sk st-sk--name"/>
            <span className="skeleton st-sk st-sk--bar"/>
            <span className="skeleton st-sk st-sk--short"/>
          </div>
        </div>
      ))}
      {drives.map((d) => (
        <div key={d.id} className="st-tree-drive">
          <div
            className={`st-drivehead ${drive === d.id ? 'is-active' : ''} ${dropTarget === `${d.id}:/` ? 'is-drop' : ''}`}
            onDragOver={(e) => { if (d.online) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } }}
            onDrop={(e) => { if (d.online) onDropFiles(e, '/', d.id); }}
          >
            <button type="button" className="st-drivehead-main" onClick={() => onNavigate(d.id, '/')} disabled={!d.online}>
              <span className="st-drivehead-top">
                <Icon name="drive" size={14}/>
                <span className="st-tree-name">{d.label}</span>
                <span className={`dot ${d.online ? 'dot--ok' : 'dot--err'}`} aria-hidden="true"/>
              </span>
              {d.online ? (
                <>
                  <span className="bar st-drivebar">
                    <span className={`fill ${d.used / d.cap > 0.9 ? 'fill--err' : d.used / d.cap > 0.75 ? 'fill--warn' : ''}`}
                          style={{ width: `${Math.max(1, Math.min(100, (d.used / d.cap) * 100))}%` }}/>
                  </span>
                  <span className="st-drivehead-meta">{fmtBytes(d.used)} of {fmtBytes(d.cap)}</span>
                </>
              ) : (
                <button type="button" className="st-drivehead-meta st-drivehead-why" onClick={(e) => { e.stopPropagation(); onOpenDrives?.(); }}>
                  {d.setup_needed ? 'not set up yet' : 'offline — why?'}
                </button>
              )}
            </button>
          </div>
          {d.online ? (
            <ul className="st-tree-children">
              {!roots[d.id] && [0, 1, 2].map((i) => (
                <li key={i} className="st-tree-skel" aria-hidden="true" style={{ paddingLeft: 26 }}>
                  <span className="skeleton st-sk st-sk--line"/>
                </li>
              ))}
              {(roots[d.id] || []).map((f) => (
                <Node key={f.id} token={token} drive={d.id} folder={f} depth={1}
                      path={drive === d.id ? path : ''} onNavigate={onNavigate}
                      cache={cache} setCache={setCache}
                      dropTarget={dropTarget} onDropFiles={(e, p) => onDropFiles(e, p, d.id)}/>
              ))}
            </ul>
          ) : (
            <div className="st-tree-offline meta">offline</div>
          )}
        </div>
      ))}
      {/* The trash, last: how much is in it, and a place to drop things. */}
      {onOpenTrash && (
        <button type="button"
                className={`st-tree-trash ${trashActive ? 'is-active' : ''} ${trashOver ? 'is-drop' : ''}`}
                onClick={onOpenTrash}
                onDragOver={(e) => { if (e.dataTransfer.types.includes('application/x-storage-rows')) { e.preventDefault(); setTrashOver(true); } }}
                onDragLeave={() => setTrashOver(false)}
                onDrop={(e) => { setTrashOver(false); onDropTrash?.(e); }}
                title={trash ? `${trash.count} item${trash.count === 1 ? '' : 's'} · cleared after ${trash.days} days` : 'Trash'}>
          <Icon name="trash" size={14}/>
          <span className="st-tree-name">Trash</span>
          {trash?.count > 0 && <span className="st-tree-trashmeta tnum">{trash.count} · {fmtBytes(trash.bytes)}</span>}
        </button>
      )}
    </nav>
  );
}
